import { Controller, useFormContext } from 'react-hook-form';
import { getBankDataFromIBAN } from 'iban-utils';
import { TextInput } from '@/components/form/TextInput.tsx';
import SingleSelectInput from '@/components/form/SingleSelectInput.tsx';
import ToggleInput from '@/components/form/ToggleInput.tsx';
import TwoInputsRow from '@/components/form/ui/TwoInputsRow.tsx';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import PrimitiveInput from '@/components/form/_primitives/PrimitiveInput.tsx';
import { CreateBankAccountInputData } from '@/features/dashboard/paymentConfig/schemas/bankAccountSchema.ts';
import { currencySelectOptions } from '@/features/dashboard/paymentConfig/types/Currency.utils.ts';
import { accountTypeOptions } from '@/features/dashboard/paymentConfig/types/AccountType.utils.ts';
import { AccountType } from '@/features/dashboard/paymentConfig/types/AccountType.ts';
import {
  formatNRB,
  formatIBANValue,
} from '@/features/dashboard/paymentConfig/types/BankAccountDto.utils.ts';

interface BankAccountFormProps {
  isEdit?: boolean;
}

const BankAccountForm = ({ isEdit = false }: BankAccountFormProps) => {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext<CreateBankAccountInputData>();

  const accountType = watch('accountType');
  const isIBAN = accountType === AccountType.IBAN;

  const handleAccountTypeChange = () => {
    setValue('accountNumber', '');
    setValue('swiftCode', '');
    setValue('bankName', '');
  };

  const autofillBankData = (stripped: string) => {
    const iban = accountType === AccountType.NRB ? 'PL' + stripped : stripped;
    const result = getBankDataFromIBAN(iban);
    if (!result?.valid || !result.bank) return;
    setValue('bankName', result.bank.name);
    if (
      accountType === AccountType.IBAN &&
      result.bank.branch?.bank_number_data?.bic_codes?.[0]
    ) {
      const bic = result.bank.branch.bank_number_data.bic_codes[0].replace(
        /^BIC\s*(SEPA\s*)?/,
        '',
      );
      setValue('swiftCode', bic);
    }
  };

  return (
    <div className="space-y-4">
      {!isEdit ? (
        <TwoInputsRow>
          <ToggleInput
            fieldPath="accountType"
            control={control}
            options={accountTypeOptions}
            label="Typ konta"
            required
            onChange={handleAccountTypeChange}
          />
          <SingleSelectInput
            fieldName="currency"
            control={control}
            options={currencySelectOptions}
            label="Waluta"
            required
            error={errors.currency?.message}
            className="w-[120px]"
          />
        </TwoInputsRow>
      ) : (
        <ToggleInput
          fieldPath="accountType"
          control={control}
          options={accountTypeOptions}
          label="Typ konta"
          required
          onChange={handleAccountTypeChange}
        />
      )}

      {isIBAN ? (
        <TwoInputsRow ratio="2/1">
          <Controller
            name="accountNumber"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col gap-1.5">
                <InputLabel label="Numer konta" required />
                <PrimitiveInput
                  value={field.value}
                  onChange={(e) => {
                    const formatted = formatIBANValue(e.target.value);
                    field.onChange(formatted);
                    autofillBankData(formatted.replace(/\s/g, ''));
                  }}
                  onBlur={field.onBlur}
                  placeholder="PL00 0000 0000 0000 0000 0000 0000"
                  error={errors.accountNumber?.message}
                />
                <InputError error={errors.accountNumber?.message} />
              </div>
            )}
          />
          <TextInput
            label="Kod SWIFT"
            required
            placeholder="BPKOPLPW"
            register={register('swiftCode')}
            error={errors.swiftCode?.message}
          />
        </TwoInputsRow>
      ) : (
        <Controller
          name="accountNumber"
          control={control}
          render={({ field }) => {
            return (
              <div className="flex flex-col gap-1.5">
                <InputLabel label="Numer konta" required />
                <PrimitiveInput
                  value={field.value}
                  onChange={(e) => {
                    const formatted = formatNRB(e.target.value);
                    field.onChange(formatted);
                    autofillBankData(formatted.replace(/\s/g, ''));
                  }}
                  onBlur={field.onBlur}
                  placeholder="00 0000 0000 0000 0000 0000 0000"
                  error={errors.accountNumber?.message}
                />
                <InputError error={errors.accountNumber?.message} />
              </div>
            );
          }}
        />
      )}

      <TextInput
        label="Nazwa banku"
        required
        placeholder="Np. PKO BP"
        register={register('bankName')}
        error={errors.bankName?.message}
      />
      <TextInput
        label="Dodatkowe informacje do przelewu"
        placeholder="Np. nazwa i adres odbiorcy, NIP"
        hint="Uczestnik zobaczy je obok numeru konta."
        register={register('companyAddress')}
        error={errors.companyAddress?.message}
      />
    </div>
  );
};

export default BankAccountForm;
